import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { RegimeTributarioEnum } from '@shared/enums/motor-fiscal/regime-tributario.enum';
import { MotorFiscalLookupService } from '@shared/services/apis/motor-fiscal/motor-fiscal-lookup.service';
import { NaturezaOperacaoSeedRequest } from '@shared/services/requests/motor-fiscal/natureza-operacao-seed.request';
import { LookupItemStruct } from '@shared/services/structs/motor-fiscal/lookup-item.struct';

import { MotivoSeedRegime, RegimeTributarioSeedDialogData } from './regime-tributario-seed-dialog-data';

export { MotivoSeedRegime, RegimeTributarioSeedDialogData } from './regime-tributario-seed-dialog-data';

/**
 * Fallback do seed de naturezas: só aparece quando o cadastro da empresa (Configurações > Empresa)
 * não responde o regime tributário. Com o cadastro preenchido, `NaturezasListaComponent` semeia
 * direto e este modal nunca abre.
 *
 * O regime é opcional no cadastro da empresa e continuará sendo, então este caminho é permanente:
 * o seed precisa de uma resposta e alguém tem que dá-la. O que o usuário informar aqui é gravado
 * no cadastro pelo componente que abriu o modal — exceto quando `gravarNoCadastro` é false.
 */
@Component({
    selector: 'app-regime-tributario-seed-dialog',
    standalone: true,
    imports: [CommonModule, MatButtonModule, MatDialogModule, MatFormFieldModule, MatIconModule, MatSelectModule],
    templateUrl: './regime-tributario-seed-dialog.component.html',
    styleUrl: './regime-tributario-seed-dialog.component.css',
})
export class RegimeTributarioSeedDialogComponent implements OnInit {
    public regimeTributarioSelecionado: number | null = null;
    public regimeApuracaoIrpjSelecionado: number | null = null;
    public regimesTributarios: LookupItemStruct[] = [];
    public regimesApuracaoIrpj: LookupItemStruct[] = [];

    constructor(
        private dialogRef: MatDialogRef<RegimeTributarioSeedDialogComponent, NaturezaOperacaoSeedRequest>,
        private motorFiscalLookupService: MotorFiscalLookupService,
        @Inject(MAT_DIALOG_DATA) public data: RegimeTributarioSeedDialogData,
    ) {}

    ngOnInit(): void {
        this.motorFiscalLookupService.getRegimesTributarios().subscribe(regimes => this.regimesTributarios = regimes);
        this.motorFiscalLookupService.getRegimesApuracaoIrpj().subscribe(regimes => this.regimesApuracaoIrpj = regimes);
    }

    /**
     * Quando o cadastro está incompleto, a pergunta é um convite a completá-lo. Quando não
     * conseguimos lê-lo, prometer isso seria falso — o título então fala só do seed.
     */
    public get titulo(): string {
        return this.data.gravarNoCadastro
            ? 'Antes de começar, vamos completar o cadastro da sua empresa?'
            : 'Informe o regime tributário da empresa';
    }

    public get isRegimeNormal(): boolean {
        return this.regimesTributarios.find(x => x.id === this.regimeTributarioSelecionado)?.codigo
            === RegimeTributarioEnum.RegimeNormal.toString();
    }

    public get podeConfirmar(): boolean {
        if (this.regimeTributarioSelecionado === null) return false;
        return !this.isRegimeNormal || this.regimeApuracaoIrpjSelecionado !== null;
    }

    public selecionarRegimeTributario(idRegimeTributario: number): void {
        this.regimeTributarioSelecionado = idRegimeTributario;
        this.regimeApuracaoIrpjSelecionado = null;
    }

    public confirmar(): void {
        if (!this.podeConfirmar) return;

        this.dialogRef.close({
            idRegimeTributario: this.regimeTributarioSelecionado!,
            idRegimeApuracaoIrpj: this.regimeApuracaoIrpjSelecionado,
        });
    }
}
